import { useEffect, useRef, useState } from "react";
import { SlidersHorizontal, ChevronLeft, ChevronRight, ChevronDown } from "lucide-react";

import CustomButton from "@/components/desktop/button/CustomButton";

const TableToolbar = ({
	styles,
	pageSize,
	pageSizeOptions,
	onChangePageSize,
	canPreviousPage,
	canNextPage,
	previousPage,
	nextPage,
	startItem,
	endItem,
	totalItems,
	isColumnsMenuVisible,
	setIsColumnsMenuVisible,
	columnsMenuRef,
	allColumns,
	actionButtons,
}) => {
	const [openDropdown, setOpenDropdown] = useState(null);
	const dropdownRefs = useRef({});

	useEffect(() => {
		const handleClickOutside = (event) => {
			const clickedOutside = Object.values(dropdownRefs.current).every((ref) => ref && !ref.contains(event.target));
			if (clickedOutside) setOpenDropdown(null);
		};
		const onEsc = (event) => event.key === "Escape" && setOpenDropdown(null);
		document.addEventListener("mousedown", handleClickOutside);
		document.addEventListener("keydown", onEsc);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
			document.removeEventListener("keydown", onEsc);
		};
	}, []);

	const handleActionClick = (button) => {
		if (!button.options || button.options.length === 0) {
			if (button.onClick) button.onClick();
		} else if (button.options.length === 1) {
			if (button.options[0].onClick) button.options[0].onClick();
		} else {
			setOpenDropdown((current) => (current === button.key ? null : button.key));
		}
	};

	return (
		<div className={styles.toolbar}>
			<div className={styles.toolbarLeft}>
				<span className={styles.smallText}>Mostrar</span>
				<select className={styles.select} value={pageSize} onChange={(e) => onChangePageSize(Number(e.target.value))} aria-label="Filas por página">
					{pageSizeOptions.map((optionValue) => (
						<option key={optionValue} value={optionValue}>
							{optionValue}
						</option>
					))}
				</select>
				<span className={styles.smallText}>por página</span>
			</div>

			<div className={styles.toolbarRight}>
				<div className={styles.toolbarPager}>
					<CustomButton
						variant="ghost"
						size="sm"
						ariaLabel="Página anterior"
						onClick={() => previousPage()}
						disabled={!canPreviousPage}
						startIcon={<ChevronLeft size={16} />}
					/>
					<span className={styles.rangeText}>
						{startItem} a {endItem} de {totalItems} elementos
					</span>
					<CustomButton
						variant="ghost"
						size="sm"
						ariaLabel="Página siguiente"
						onClick={() => nextPage()}
						disabled={!canNextPage}
						startIcon={<ChevronRight size={16} />}
					/>
				</div>

				<div className={styles.dropdown} ref={columnsMenuRef}>
					<CustomButton
						variant="secondary"
						size="sm"
						onClick={() => setIsColumnsMenuVisible((s) => !s)}
						ariaLabel="Mostrar u ocultar columnas"
						startIcon={<SlidersHorizontal size={18} />}
					>
						Columnas
					</CustomButton>

					{isColumnsMenuVisible && (
						<div className={styles.menu} role="menu">
							<div className={styles.menuHeader}>Mostrar u Ocultar</div>
							<ul className={styles.menuList}>
								{allColumns
									.filter((column) => column.id !== "_selector")
									.map((column) => (
										<li key={column.id} className={styles.menuItem}>
											<label className={styles.checkbox}>
												<input type="checkbox" {...column.getToggleHiddenProps()} />
												<span>{column.Header || column.id}</span>
											</label>
										</li>
									))}
							</ul>
						</div>
					)}
				</div>

				{actionButtons?.length > 0 && (
					<div className={styles.toolbarActions}>
						{actionButtons.map((button) => {
							const hasOptions = button.options && button.options.length > 1;
							const isOpen = openDropdown === button.key;
							const ButtonIcon = button.icon;
							return (
								<div key={button.key} className={styles.toolbarActionsDropdown} ref={(el) => (dropdownRefs.current[button.key] = el)}>
									<CustomButton
										variant={button.variant || "secondary"}
										size="sm"
										startIcon={ButtonIcon ? <ButtonIcon strokeWidth={2} size={16} /> : undefined}
										endIcon={hasOptions ? <ChevronDown strokeWidth={2} size={14} /> : undefined}
										onClick={() => handleActionClick(button)}
										disabled={button.disabled}
										aria-haspopup={hasOptions ? "menu" : undefined}
										aria-expanded={hasOptions ? isOpen : undefined}
									>
										{button.label}
									</CustomButton>
									{isOpen && hasOptions && (
										<ul className={styles.toolbarActionsMenu} role="menu">
											{button.options.map((opt) => {
												const OptionIcon = opt.icon;
												return (
													<li key={opt.key} role="none">
														<button
															role="menuitem"
															type="button"
															className={styles.toolbarActionsMenuItem}
															onClick={() => {
																if (opt.onClick) opt.onClick();
																setOpenDropdown(null);
															}}
														>
															{OptionIcon && <OptionIcon strokeWidth={2} size={14} />}
															<span>{opt.label}</span>
														</button>
													</li>
												);
											})}
										</ul>
									)}
								</div>
							);
						})}
					</div>
				)}
			</div>
		</div>
	);
};

export default TableToolbar;
